import { useState } from 'react';
import './Page.css';

export default function APPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faq = [
    {
      q: 'Are the AP question banks available yet?',
      a: 'AP question banks are coming soon. In the meantime, our expert AP tutors are available now for live 1:1 sessions covering any AP course.',
    },
    {
      q: 'Which AP courses does tutoring cover?',
      a: 'Our tutors cover a wide range of AP courses. Book a free consultation and we\'ll match you with a tutor who specializes in the specific AP course you need.',
    },
    {
      q: 'Is 1:1 tutoring available for AP courses?',
      a: 'Yes. Our tutors work with students on AP courses across all subject areas. Sessions are tailored to your exact weak areas and exam timeline. Book a free consultation to get started.',
    },
    {
      q: 'Will this help me get a 4 or 5 on the AP exam?',
      a: 'Students who work consistently with a tutor see significant improvement. Your tutor will build a plan around your weak areas and exam timeline, focusing on exactly what the AP exam tests.',
    },
    {
      q: 'Can I use this alongside my AP class?',
      a: 'Absolutely. Our tutors work around your class schedule and coordinate with what you\'re currently covering in class. The SAT question bank is available now if you want to add self-paced practice to your routine.',
    },
    {
      q: 'Will more AP question banks be added?',
      a: 'Yes. AP question banks are coming soon. Tutoring is available for all AP courses right now.',
    },
  ];

  return (
    <div className="page-wrap">

      {/* ── Hero ── */}
      <div className="exam-hero">
        <div className="exam-hero-inner">
          <div className="exam-hero-text">
            <div className="exam-hero-badge">AP Exam Prep</div>
            <h1>Expert AP tutoring.<br />Question banks coming soon.</h1>
            <p className="exam-hero-sub">
              AP question banks are on the way. While you wait, work with a live expert tutor
              who knows the College Board curriculum and can target exactly where you're losing points —
              for any AP course.
            </p>
            <p className="exam-hero-proof">★★★★★ &nbsp; Any AP course · Live 1:1 · Expert tutors</p>
            <div className="exam-hero-btns">
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
              <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
            </div>
          </div>
          <div className="exam-hero-visual" style={{ alignItems: 'center', paddingBottom: 32, display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center' }}>
            <div style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: '28px 32px', maxWidth: 380, width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                <div style={{ background: '#f59e0b', color: '#78350f', fontSize: 11, fontWeight: 800, padding: '4px 12px', borderRadius: 20 }}>COMING SOON</div>
                <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13 }}>AP Question Banks</span>
              </div>
              <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>
                We're building comprehensive AP question banks organized by topic, with full review mode and AI-powered explanations.
              </p>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16 }}>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: 13, marginBottom: 0 }}>
                  Expert AP tutors available <strong style={{ color: 'rgba(255,255,255,0.85)' }}>right now</strong> for any AP course.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="exam-stats-strip">
        <div className="container">
          <div className="exam-stats-inner">
            <div>
              <div className="exam-stat-num">Any Course</div>
              <div className="exam-stat-label">AP Tutoring Available</div>
            </div>
            <div>
              <div className="exam-stat-num">$55/hr</div>
              <div className="exam-stat-label">Live Expert Tutoring</div>
            </div>
            <div>
              <div className="exam-stat-num">97%</div>
              <div className="exam-stat-label">Students Improve</div>
            </div>
            <div>
              <div className="exam-stat-num">Coming Soon</div>
              <div className="exam-stat-label">AP Question Banks</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container page-body">

        {/* ── Tutoring feature ── */}
        <div className="feature-row">
          <div className="feature-row-text">
            <div className="feature-row-eyebrow">1:1 Expert Tutoring</div>
            <h2>A real AP expert, focused on your score.</h2>
            <p>
              Our AP tutors don't follow a generic script. Every session is built around your
              specific weak areas and exam timeline — for any AP course. Live on Zoom,
              flexible scheduling, and no long-term commitment required.
            </p>
            <ul className="check-list">
              <li>Expert tutors across all AP subject areas</li>
              <li>Sessions tailored to your weak areas and exam date</li>
              <li>Conceptual deep-dives + exam strategy</li>
              <li>Free-response and essay coaching for top scores</li>
              <li>Flexible scheduling around your class schedule</li>
            </ul>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
          </div>
          <div className="feature-row-visual" style={{ display: 'flex', flexDirection: 'column', gap: 16, justifyContent: 'center', background: 'transparent', border: 'none', padding: 0 }}>
            <div style={{ background: 'var(--navy)', borderRadius: 16, padding: 28, color: '#fff' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>While the question banks are in development…</div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {[
                  'Work 1:1 with an expert AP tutor',
                  'Get a personalized study plan for your exam date',
                  'Target your exact weak spots by topic',
                  'Exam strategy + free-response coaching',
                  'SAT question bank available right now',
                ].map(f => (
                  <li key={f} style={{ fontSize: 14, color: 'rgba(255,255,255,0.85)', paddingLeft: 20, position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 0, color: 'var(--gold)' }}>✓</span>{f}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Testimonials ── */}
        <div className="page-section">
          <div className="mini-test-grid">
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
              <p className="mini-test-quote">"My tutor really helped me break down the material step by step. After a few weeks, I actually started to enjoy the subject — and I ended up getting an A in the class."</p>
              <div className="mini-test-name">Jordan T. — Class of 2024</div>
            </div>
            <div className="mini-test-card">
              <div style={{ color: 'var(--gold)', marginBottom: 8 }}>★★★★★</div>
              <p className="mini-test-quote">"I take regular sessions with my tutor before exams. Those sessions make a big difference — I've been able to keep my grade at an A."</p>
              <div className="mini-test-name">Anika S. — Student</div>
            </div>
          </div>
        </div>

        {/* ── Pricing ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 28 }}>
            <span className="section-label">Get Started</span>
            <h2 className="section-heading" style={{ marginBottom: 8 }}>Start improving your AP score now.</h2>
            <p className="section-sub">Expert tutoring available today. Question banks coming soon.</p>
          </div>
          <div className="two-col-section" style={{ marginBottom: 0 }}>
            <div className="path-card featured">
              <div className="path-icon">👨‍🏫</div>
              <div className="path-name">1:1 Expert Tutoring</div>
              <div className="path-tagline">AP tutors, live on Zoom — available now</div>
              <div className="path-price">$55<span style={{ fontSize: 16, fontWeight: 400 }}>/hour</span></div>
              <div className="path-price-note">10 &amp; 20-hour bundles available</div>
              <ul className="path-features">
                <li>Expert tutors across all AP subject areas</li>
                <li>Sessions built around your weak areas</li>
                <li>Conceptual deep-dives on your weak topics</li>
                <li>Exam strategy and free-response coaching</li>
                <li>Flexible scheduling around your class schedule</li>
              </ul>
              <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold" style={{ textAlign: 'center' }}>Book a Free Consultation</a>
            </div>
            <div className="path-card" style={{ opacity: 0.75 }}>
              <div className="path-icon">📚</div>
              <div className="path-name">AP Question Banks</div>
              <div className="path-tagline" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ background: '#f59e0b', color: '#78350f', fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 20 }}>COMING SOON</span>
              </div>
              <div className="path-price" style={{ color: 'var(--text-gray)' }}>—</div>
              <div className="path-price-note">Launching soon · Free tier planned</div>
              <ul className="path-features">
                <li>Questions organized by topic &amp; subtopic</li>
                <li>Aligned to College Board curriculum</li>
                <li>Full review mode after every session</li>
                <li>Aira AI chatbot explains every answer</li>
                <li>Topic-by-topic accuracy tracking</li>
              </ul>
              <a href="/signup" className="btn-navy" style={{ textAlign: 'center' }}>Create Free Account</a>
            </div>
          </div>
        </div>

        {/* ── FAQ ── */}
        <div className="page-section">
          <div className="text-center" style={{ marginBottom: 24 }}>
            <h2 className="section-heading">Frequently Asked Questions</h2>
          </div>
          <div className="faq-list">
            {faq.map((item, i) => (
              <details
                key={i}
                className="faq-item"
                open={openFaq === i}
                onClick={(e) => { e.preventDefault(); setOpenFaq(openFaq === i ? null : i); }}
              >
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="cta-strip" style={{ marginBottom: 32 }}>
          <h3>Get expert AP tutoring today.</h3>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15, marginBottom: 24 }}>
            Question banks coming soon. Live 1:1 tutoring available right now for any AP course.
          </p>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://secure.tutorcruncher.com/scholar-sphere/signup/client/" target="_blank" rel="noreferrer" className="btn-gold">Book a Free Consultation</a>
            <a href="/contact" className="btn-outline-white">Talk to an Expert →</a>
          </div>
        </div>

      </div>
    </div>
  );
}
